import type { Notification } from "@/composables/notification/useNotifications"

export interface GroupedNotification {
  id: string // ID of the primary notification
  type: string
  reference_id: string | null
  message: string
  read: boolean
  created_at: string // Most recent notification time
  actor_id: string | null
  actor_username: string | null
  actor_profile_pic_url: string | null
  metadata: Record<string, any>
  // Grouping-specific fields
  count: number // Total number of notifications in this group
  additionalActors: Array<{
    id: string
    username: string | null
    profile_pic_url: string | null
  }> // Other actors who performed the same action
  notifications: Notification[] // All notifications in this group
}

/**
 * Group notifications by type and reference_id (Instagram-style)
 * - activity_like: Group all likes on the same activity
 * - activity_comment: Group all comments on the same activity
 * - Other types: No grouping
 */
export function groupNotifications(notifications: Notification[]): GroupedNotification[] {
  const grouped: GroupedNotification[] = []
  const groupedSet = new Set<string>()

  // Types that should be grouped
  const groupableTypes = new Set(["activity_like", "activity_comment"])

  for (const notification of notifications) {
    // Skip if already processed
    if (groupedSet.has(notification.id)) {
      continue
    }

    // Check if this type should be grouped
    if (groupableTypes.has(notification.type) && notification.reference_id) {
      // Find all notifications with same type and reference_id
      const relatedNotifications = notifications.filter(
        (n) => n.type === notification.type && n.reference_id === notification.reference_id,
      )

      // Mark all as processed
      relatedNotifications.forEach((n) => groupedSet.add(n.id))

      // Sort by created_at (newest first)
      relatedNotifications.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )

      // Get the primary notification (most recent)
      const primary = relatedNotifications[0]
      if (!primary) continue // Skip if no primary (shouldn't happen)

      // Get additional actors (excluding the primary)
      const additionalActors = relatedNotifications
        .slice(1)
        .map((n) => ({
          id: n.actor_id || "",
          username: n.actor_username,
          profile_pic_url: n.actor_profile_pic_url,
        }))
        .filter((actor) => actor.id) // Remove entries without actor_id

      // Determine if any notification in the group is unread
      const hasUnread = relatedNotifications.some((n) => !n.read)

      // Generate grouped message
      const groupedMessage = generateGroupedMessage(
        notification.type,
        primary.actor_username || "Someone",
        relatedNotifications.length,
      )

      grouped.push({
        id: primary.id,
        type: primary.type,
        reference_id: primary.reference_id,
        message: groupedMessage,
        read: !hasUnread, // Unread if ANY notification in group is unread
        created_at: primary.created_at,
        actor_id: primary.actor_id,
        actor_username: primary.actor_username,
        actor_profile_pic_url: primary.actor_profile_pic_url,
        metadata: primary.metadata,
        count: relatedNotifications.length,
        additionalActors,
        notifications: relatedNotifications,
      })
    } else {
      // Not groupable - add as-is but in GroupedNotification format
      groupedSet.add(notification.id)
      grouped.push({
        id: notification.id,
        type: notification.type,
        reference_id: notification.reference_id,
        message: notification.message,
        read: notification.read,
        created_at: notification.created_at,
        actor_id: notification.actor_id,
        actor_username: notification.actor_username,
        actor_profile_pic_url: notification.actor_profile_pic_url,
        metadata: notification.metadata,
        count: 1,
        additionalActors: [],
        notifications: [notification],
      })
    }
  }

  return grouped
}

/**
 * Generate a grouped message based on count
 */
function generateGroupedMessage(type: string, primaryActor: string, count: number): string {
  if (count === 1) {
    // Single notification - use original message format
    if (type === "activity_like") {
      return `${primaryActor} liked your activity`
    }
    if (type === "activity_comment") {
      return `${primaryActor} commented on your activity`
    }
    return ""
  }

  // Multiple notifications - grouped message
  const others = count - 1
  if (type === "activity_like") {
    return `${primaryActor} and ${others} ${others === 1 ? "other" : "others"} liked your activity`
  }
  if (type === "activity_comment") {
    return `${primaryActor} and ${others} ${others === 1 ? "other" : "others"} commented on your activity`
  }

  return ""
}
